import { View, GradientModal, Text } from '@/components'
import { shareOrganisation, useModalWithProps } from '@/utils'
import { ClimateEngagementScore } from '../..'
import { ReviewResultsCard } from '../molecules'
import { useAppI18N, variantProvider } from '@/app'
import { ScoreTopic } from '@/types'
import React from 'react'
import { Organisation } from '@/features'
import { APIClient } from '@/services'

type ScoreModalProps = {
  industryId?: Organisation.T.OrganisationCategory['slug']
  organisationId?: Organisation.T.Organisation['id']
  topicId?: ScoreTopic['value']
  visible?: boolean
  toggle?: () => void
}

export const ScoreModal = (props: ScoreModalProps) => {
  const { visible, toggle, topicId, industryId, organisationId } = props

  const { categories, subcategories } = APIClient.Organisations.useCategories({ topic: topicId }, visible)

  const score = APIClient.Organisations.useScores(
    {
      category: industryId,
      organisation: organisationId,
      topics: topicId
    },
    visible
  )

  const organisation = APIClient.Organisations.organisationsManager.useItem({
    id: organisationId,
    queryOptions: {
      enabled: visible
    }
  })

  const topic = APIClient.Organisations.useScoreTopic(topicId)

  const { t } = useAppI18N()

  if (!categories || score.query.isLoading) return null

  const categoriesArr = Object.values(categories)

  return (
    <GradientModal
      toggle={() => toggle()}
      visible={visible}
      title={topic?.data?.label}
      onSharePress={organisationId ? () => shareOrganisation(organisation?.data) : undefined}
      debugName={'GradientScoreModal'}
      bodyStyle={styles.body}
      boxStyle={styles.box}
      headerStyle={styles.header}
    >
      <>
        <Text variants={['p1']} text={topic?.data?.description} />
        <View style={styles.wrapper}>
          <View
            variants={['column', 'gap:2', 'center', 'fullWidth', 'marginBottom:4']}
            responsiveVariants={{ small: ['marginBottom:2'] }}
          >
            <ClimateEngagementScore variant={'withoutModal'} score={score.score} topic={topic.data} />
          </View>
          {categoriesArr.map(item => (
            <ReviewResultsCard
              key={item.id}
              category={item}
              score={score.score?.categories[item.category]}
              topic={topic?.data}
              subcategories={Object.values(subcategories).filter(c => c.category == item.id)}
            />
          ))}
          {/*
          {hasNps ? (
            <View style={[Theme.effects.sectionElevation]} variants={['bg:neutral1', 'fullWidth', 'padding:2', 'border-radius:small', 'fullWidth', 'column']}>
              <ReviewResultsHeader
                score={organisation?.nps?.score}
                img={AppImages.ClimateNPS}
                description={t('climateNPS.description')}
                name={t('climateNPS.title')}
              />
              <Text
                variants={['p4', 'alignSelfCenter', 'marginTop:1']}
                text={t('climateNPS.responses', { qtd: organisation?.nps?.number_of_contributors })}
              />
            </View>
          ) : null} */}
        </View>
      </>
    </GradientModal>
  )
}

type Params = Pick<ScoreModalProps, 'industryId' | 'topicId' | 'organisationId'>

export const useScoreModal = (visible?: boolean, initialParams: Partial<Params> = {}) =>
  useModalWithProps<Params>(initialParams, visible)

const styles = variantProvider.createComponentStyle(
  theme => ({
    body: {
      overflow: 'auto',
      maxHeight: `calc(100dvh - 56px)`,

      [theme.media.up('small')]: {
        padding: theme.spacing.value(4),
        paddingTop: theme.spacing.value(0)
      }
    },
    header: {
      [theme.media.up('small')]: {
        paddingLeft: theme.spacing.value(4),
        paddingRight: theme.spacing.value(4)
      }
    },
    box: {
      [theme.media.up('small')]: {
        padding: theme.spacing.value(0),
        paddingTop: theme.spacing.value(4)
      }
    },
    wrapper: {
      ...theme.presets.column,
      ...theme.spacing.gap(2),
      ...theme.spacing.marginTop(3),
      maxWidth: 'calc(100vw - 32px)'
    }
  }),
  true
)
